import React, { useEffect, useState } from 'react';

import { Button, Grid, Link, TextField } from '@mui/material';
import { Alert } from '@mui/material';
import { filter, flow, map } from 'lodash/fp';
import AutocompleteSearch from 'views/I9/components/AutocompleteSearch';
import FileUpload from 'views/I9/components/FileUpload';
import OneTimeAutocompleteSearch from 'views/I9/components/OneTimeAutocompleteSearch';
import ResourceList from 'views/I9/components/ResourceList';
import axios from 'views/I9/modules/axios';
import mandats from 'views/I9/resources/mandats';

import TransferList from './TransferList';

const INITIAL_EDIT_STATUS_MANDAT = { disableUpdate: false, message: '' };
const ID_MANDAT_PROGRAMME = 2;

const isMandatProgramme = id => id === ID_MANDAT_PROGRAMME;

const EditMandat = ({ values, props, setFieldValue, setFieldTouched, touched, initialValues, handleSubmit }) => {
  const [editStatutMandat, setCanEditMandat] = useState(INITIAL_EDIT_STATUS_MANDAT);
  const [progsLoaded, setProgsLoaded] = useState(false);
  const [leftInitialProgs, setLeftInitialProgs] = useState([]);
  const [rightInitialProgs, setRightInitialProgs] = useState([]);

  // Check if the mandat can be saved based on current params
  useEffect(() => {
    const checkMandat = async () => {
      const res = await axios.get(
        `verifPromoteur?idmandat=${values.idmandat}&idpromoteur=${values.idpromoteur}&type=${
          values.mandat_commercialisation_mandat_type_id
        }&${map(id => `listIdProg[]=${id}`, values.selected_programmes).join('&')}`,
      );

      if (res.data.message) {
        setCanEditMandat({
          disableUpdate: true,
          message: res.data.message,
        });

        return;
      }

      setCanEditMandat(INITIAL_EDIT_STATUS_MANDAT);
    };

    if (touched.idpromoteur || touched.mandat_commercialisation_mandat_type_id) {
      checkMandat();
    }
  }, [
    touched,
    values.idmandat,
    values.idpromoteur,
    values.mandat_commercialisation_mandat_type_id,
    values.selected_programmes,
    rightInitialProgs,
  ]);

  useEffect(() => {
    const loadProg = async idpromoteur => {
      const res = await axios.get(
        `prog?_associations=*statut&idpromoteur=${idpromoteur}&_fields=id,nom,dept&statut._fields=prog_statut_short_name`,
      );
      const mandatProgs = await axios.get(
        `mandat_prog?mandat_prog_idmandat=${values.idmandat}&_fields=mandat_prog_idprog`,
      );

      const rightIds = map('mandat_prog_idprog', mandatProgs.data);

      setRightInitialProgs(
        flow(
          map(item => ({
            id: item.id,
            nom: `${item.nom} - ${item.dept} (${item.statut.prog_statut_short_name})`,
          })),
          // Keep those already in right list
          filter(({ id }) => rightIds.includes(id)),
        )(res.data),
      );

      setLeftInitialProgs(
        flow(
          map(item => ({
            id: item.id,
            nom: `${item.nom} - ${item.dept} (${item.statut.prog_statut_short_name})`,
          })),
          // Remove those already in right list
          filter(({ id }) => !rightIds.includes(id)),
        )(res.data),
      );
      setProgsLoaded(true);
    };

    if (values.idpromoteur && isMandatProgramme(values.mandat_commercialisation_mandat_type_id)) {
      loadProg(values.idpromoteur);
    }
  }, [values.idmandat, values.idpromoteur, values.mandat_commercialisation_mandat_type_id]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Grid container justifyContent='flex-end'>
          {values.mandat_commercialisation_deleted ? (
            <Link
              component='button'
              variant='body2'
              onClick={() => {
                setFieldValue('mandat_commercialisation_deleted', 0);
                handleSubmit();
              }}
            >
              Rouvrir le mandat
            </Link>
          ) : (
            <Button
              color='secondary'
              onClick={() => {
                setFieldValue('mandat_commercialisation_deleted', 1);
                handleSubmit();
              }}
            >
              Cloturer le mandat
            </Button>
          )}
        </Grid>
      </Grid>
      {editStatutMandat.disableUpdate && (
        <Grid item xs={12}>
          <Alert severity='error'>{editStatutMandat.message}</Alert>
        </Grid>
      )}
      <Grid item xs={2}>
        <TextField {...props} name='num' label='Num' value={values.num} />
      </Grid>
      <Grid item xs={10}>
        <AutocompleteSearch
          {...props}
          fullWidth
          resource='promoteur'
          attribute='nom'
          label='Promoteur'
          attributeId='idpromoteur'
          value={values.idpromoteur}
          onChange={(event, value) => {
            setFieldValue('idpromoteur', value?.idpromoteur);
            setFieldTouched('idpromoteur', true);
            if (!value?.idpromoteur) {
              setProgsLoaded(false);
            }
          }}
          filters={{
            _fields: 'idpromoteur,nom',
          }}
          disabled={initialValues.idpromoteur !== 0}
        />
      </Grid>
      {values.url_serveur ? (
        <Grid item xs={12}>
          <a href={values.url_serveur} target='_blank' rel='noopener noreferrer'>
            Mandat
          </a>
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <FileUpload
          resource='mandat_commercialisation'
          id={values.idmandat}
          attribute='url_serveur'
          textButton='Charger un mandat'
          onUpload={changes => {
            setFieldValue('url_serveur', changes.url_serveur);
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          {...props}
          fullWidth
          name='date_signature'
          type='date'
          label='Date signature'
          value={values.date_signature}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField {...props} fullWidth name='date_fin' type='date' label='Date fin' value={values.date_fin} />
      </Grid>
      <Grid item xs={12} md={4}>
        <OneTimeAutocompleteSearch
          resource='mandat_type'
          attributeId='id'
          attribute='nom'
          value={values.mandat_commercialisation_mandat_type_id}
          label='Type de mandat'
          onChange={(event, value) => {
            if (value) {
              setFieldValue('mandat_commercialisation_mandat_type_id', value.id);
              setFieldTouched('mandat_commercialisation_mandat_type_id', true);
            }
            if (!isMandatProgramme(value.id)) {
              setProgsLoaded(false);
            }
          }}
          size='small'
          disabled={initialValues.mandat_commercialisation_mandat_type_id !== 0}
        />
      </Grid>
      {progsLoaded && (
        <TransferList
          dataLeft={leftInitialProgs}
          dataRight={rightInitialProgs}
          titleLeft='Programmes disponibles'
          titleRight='Programmes sélectionnés'
          rightListUpdate={list => setFieldValue('selected_programmes', list)}
        />
      )}

      {values.mandat_commercialisation_idmandat === 0 ? (
        <Grid item xs={12}>
          <h3>Avenants</h3>
          <ResourceList
            {...mandats}
            filters={{ mandat_commercialisation_idmandat: values.idmandat, idpromoteur: values.idpromoteur }}
          />
        </Grid>
      ) : null}
    </Grid>
  );
};

export default EditMandat;
